'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

interface ColorOption {
  name: string;
  hex: string;
  digit?: number;
  multiplier?: number;
}

const DIGIT_COLORS: ColorOption[] = [
  { name: 'Black', hex: '#1a1a1a', digit: 0, multiplier: 0 },
  { name: 'Brown', hex: '#7b4a1e', digit: 1, multiplier: 1 },
  { name: 'Red', hex: '#e33232', digit: 2, multiplier: 2 },
  { name: 'Orange', hex: '#f2802e', digit: 3, multiplier: 3 },
  { name: 'Yellow', hex: '#f2d02e', digit: 4, multiplier: 4 },
  { name: 'Green', hex: '#2e9e44', digit: 5, multiplier: 5 },
  { name: 'Blue', hex: '#2e6ee3', digit: 6, multiplier: 6 },
  { name: 'Violet', hex: '#8b2ee3', digit: 7, multiplier: 7 },
  { name: 'Gray', hex: '#8c8c8c', digit: 8, multiplier: 8 },
  { name: 'White', hex: '#f5f5f5', digit: 9, multiplier: 9 },
];

const MULTIPLIER_COLORS: ColorOption[] = [
  ...DIGIT_COLORS,
  { name: 'Gold', hex: '#d4af37', multiplier: -1 },
  { name: 'Silver', hex: '#c0c0c0', multiplier: -2 },
];

const TOLERANCE_COLORS: Array<{ name: string; hex: string; tolerance: string }> = [
  { name: 'Brown', hex: '#7b4a1e', tolerance: '±1%' },
  { name: 'Red', hex: '#e33232', tolerance: '±2%' },
  { name: 'Green', hex: '#2e9e44', tolerance: '±0.5%' },
  { name: 'Blue', hex: '#2e6ee3', tolerance: '±0.25%' },
  { name: 'Violet', hex: '#8b2ee3', tolerance: '±0.1%' },
  { name: 'Gold', hex: '#d4af37', tolerance: '±5%' },
  { name: 'Silver', hex: '#c0c0c0', tolerance: '±10%' },
  { name: 'None', hex: 'transparent', tolerance: '±20%' },
];

function formatOhms(value: number) {
  if (value >= 1e6) return `${(value / 1e6).toFixed(2).replace(/\.?0+$/, '')} MΩ`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2).replace(/\.?0+$/, '')} kΩ`;
  return `${value.toFixed(2).replace(/\.?0+$/, '')} Ω`;
}

export default function BrowserResistorColorCode() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('resistor-color-code-calculator', '').catch(() => undefined);
  };

  const [band1, setBand1] = useState(1); // Brown
  const [band2, setBand2] = useState(0); // Black
  const [multiplierIdx, setMultiplierIdx] = useState(2); // Red = x100
  const [toleranceIdx, setToleranceIdx] = useState(0); // Brown = ±1%

  function onChange(setter: (v: number) => void) {
    return (v: number) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const d1 = DIGIT_COLORS[band1]?.digit ?? 0;
    const d2 = DIGIT_COLORS[band2]?.digit ?? 0;
    const mult = MULTIPLIER_COLORS[multiplierIdx]?.multiplier ?? 0;
    const ohms = (d1 * 10 + d2) * 10 ** mult;
    return { ohms, formatted: formatOhms(ohms) };
  }, [band1, band2, multiplierIdx]);

  const swatch = (color: ColorOption | { hex: string }, width = 14) => (
    <span
      style={{
        display: 'inline-block',
        width,
        height: '2.4rem',
        background:
          color.hex === 'transparent'
            ? 'repeating-linear-gradient(45deg,#444,#444 3px,#666 3px,#666 6px)'
            : color.hex,
        border: '1px solid rgba(255,255,255,0.15)',
      }}
    />
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Electronics</span>
        <h2 className="tool-live-title">Resistor color code calculator</h2>
        <p className="tool-live-subtitle">
          Pick the 4 band colors on a resistor to read its resistance and tolerance.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Band 1 (1st digit)</span>
            <select onChange={(e) => onChange(setBand1)(Number(e.target.value))} value={band1}>
              {DIGIT_COLORS.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name} ({c.digit})
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>Band 2 (2nd digit)</span>
            <select onChange={(e) => onChange(setBand2)(Number(e.target.value))} value={band2}>
              {DIGIT_COLORS.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name} ({c.digit})
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>Band 3 (multiplier)</span>
            <select
              onChange={(e) => onChange(setMultiplierIdx)(Number(e.target.value))}
              value={multiplierIdx}
            >
              {MULTIPLIER_COLORS.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name} (x10^{c.multiplier})
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>Band 4 (tolerance)</span>
            <select
              onChange={(e) => onChange(setToleranceIdx)(Number(e.target.value))}
              value={toleranceIdx}
            >
              {TOLERANCE_COLORS.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name} ({c.tolerance})
                </option>
              ))}
            </select>
          </label>

          <div
            className="tool-live-field-full"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: '1rem',
              background: '#d9b88a',
              borderRadius: '0.75rem',
              width: 'fit-content',
            }}
          >
            {swatch(DIGIT_COLORS[band1], 16)}
            {swatch(DIGIT_COLORS[band2], 16)}
            {swatch(MULTIPLIER_COLORS[multiplierIdx], 16)}
            <span style={{ width: 10 }} />
            {swatch(TOLERANCE_COLORS[toleranceIdx], 16)}
          </div>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.formatted}</strong>
            <span>{TOLERANCE_COLORS[toleranceIdx].tolerance} tolerance</span>
          </p>
          <p className="tool-live-panel-note">
            Reading order matters: band 1 is closest to one end of the resistor. If a reading looks
            wrong, try the bands in reverse order.
          </p>
        </section>
      </div>
    </div>
  );
}
